import React, { ReactElement } from "react";
import {
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import {
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";

/**
 * Props for the TextFileViewer component
 * @interface TextFileViewerProps
 * @property {string} fileUrl - The URL of the text file to be displayed
 * @property {string} fileName - The name of the file to be displayed in the header
 * @property {() => void} onClose - Callback function to handle closing the viewer
 */
interface TextFileViewerProps {
  fileUrl: string;
  fileName: string;
  onClose: () => void;
}

/**
 * A React component for displaying text files in a modal dialog with syntax highlighting
 * and responsive layout. Handles loading states and error scenarios.
 * 
 * @component
 * @param {TextFileViewerProps} props - The component props
 * @returns {JSX.Element} A dialog containing the text file content
 */
const TextFileViewer: React.FC<TextFileViewerProps> = ({
  fileUrl,
  fileName,
  onClose,
}) => {
  const [content, setContent] = React.useState<string>("");
  const [loading, setLoading] = React.useState<boolean>(true);
  const [error, setError] = React.useState<string | null>(null);

  /**
   * Renders the appropriate content based on the current state (loading, error, or content)
   * @returns {ReactElement} The content to be rendered
   */
  const renderContent = (): ReactElement => {
    if (loading) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            color: "#d4d4d4",
          }}
        >
          <Typography>Loading file content...</Typography>
        </Box>
      );
    }

    if (error) {
      return (
        <Box sx={{ p: 3, color: "#f48771" }}>
          <Typography>{error}</Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ p: 2 }}>
        {content || <Typography color="textSecondary">(Empty file)</Typography>}
      </Box>
    );
  };

  // Fetch the text file content when the component mounts or when fileUrl changes
  React.useEffect(() => {
    /**
     * Fetches the content of the text file from the provided URL
     * @async
     * @function fetchTextFile
     * @returns {Promise<void>}
     */
    const fetchTextFile = async () => {
      try {
        const response = await fetch(fileUrl);
        if (!response.ok) {
          throw new Error(`Failed to load file: ${response.statusText}`);
        }
        const text = await response.text();
        setContent(text);
      } catch (err) {
        console.error("Error loading text file:", err);
        setError(
          "Failed to load the text file. The file may be corrupted or the URL is invalid."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchTextFile();
  }, [fileUrl]);

  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          style: {
            height: "90vh",
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "rgba(0, 0, 0, 0.8)",
          color: "white",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <IconButton
            aria-label="back"
            onClick={onClose}
            sx={{
              color: "white",
              backgroundColor: "rgba(0, 0, 0, 0.7)",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.9)",
              },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" noWrap sx={{ maxWidth: "600px" }}>
            {fileName}
          </Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: "white",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.1)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          flex: 1,
          p: 0,
          backgroundColor: "#1e1e1e",
          color: "#d4d4d4",
          fontFamily: 'Consolas, "Courier New", monospace',
          fontSize: "14px",
          lineHeight: "1.5",
          whiteSpace: "pre-wrap",
          overflow: "auto",
          "&::-webkit-scrollbar": {
            width: "10px",
            height: "10px",
          },
          "&::-webkit-scrollbar-track": {
            background: "#1e1e1e",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#404040",
            "&:hover": {
              background: "#4f4f4f",
            },
          },
        }}
      >
        {renderContent()}
      </DialogContent>
    </Dialog>
  );
};

export default TextFileViewer;
